import {
  EXPANDED_EDITOR_FOOTER,
  EXPANDED_EDITOR_ACTIONS,
} from "../expanded-editor-footer.js";
import { useState } from "react";
import {
  widgetTargetKey,
  type EscalationCell,
  type EscalationTone,
} from "@playbookai/widget-schemas";
import { Button } from "../../atoms/Button/index.js";
import { Textarea } from "../../atoms/Textarea/index.js";
import { FormField } from "../../molecules/FormField/index.js";
import { cn } from "../../lib/utils.js";
import { useFieldEditSession } from "../field-edit-session.js";
import { severityClass, toneClass } from "./presentation.js";

export function ResponseDetails({
  cell,
  selection,
  readOnly,
  onDirty,
  onCancel,
  onSave,
}: {
  cell: EscalationCell | undefined;
  selection: Pick<EscalationCell, "severity" | "level">;
  readOnly: boolean;
  onDirty: () => void;
  onCancel: () => void;
  onSave: (cell: EscalationCell | undefined) => void;
}) {
  const [draft, setDraft] = useState<EscalationCell>();
  const [baseline, setBaseline] = useState<string>();
  const shown: EscalationCell = draft ?? cell ?? selection;
  /*
   * PB-4923 AC5. Two distinct cases lease NOTHING here, and neither is a
   * fallback — in both, no operation can name the thing being edited:
   *
   * - An UNSET coordinate has no cell at all; `shown` falls back to
   *   `selection`, which carries a severity and a level but no identity.
   * - A LEGACY cell has no `id`. `EscalationCell.id` is `idString.optional()`
   *   precisely so pre-backfill rows still validate, and the executor resolves
   *   an item by `widgetItemIdentity(spec, entry) === leaf.itemId`, which
   *   answers `null` for an id-less entry. So no `set_field`, `remove_item` or
   *   `move_item` can ever address that cell, and there is no AI write to defer
   *   to. Minting an id would be strictly worse: an unstable key, regenerated
   *   every render, naming a target that does not exist.
   *
   * `severity`/`level` are the SELECTION, not fields of this form, so they are
   * not leased either.
   */
  const target = (field: string): string | undefined =>
    cell?.id === undefined
      ? undefined
      : widgetTargetKey([
          { kind: "item", collection: "cells", itemId: cell.id },
          { kind: "prop", name: field },
        ]);
  const labelLease = useFieldEditSession(target("label"));
  const toneLease = useFieldEditSession(target("tone"));
  const conflict = !!draft && JSON.stringify(cell) !== baseline;
  const update = (patch: Partial<EscalationCell>) => {
    if (readOnly) return;
    if (!draft) setBaseline(JSON.stringify(cell));
    setDraft({ ...shown, ...patch });
    onDirty();
  };
  const label = shown.label ?? "";
  const tone = shown.tone ?? "neutral";
  return (
    <form
      className="@container flex min-h-full flex-col gap-5"
      onSubmit={(event) => {
        event.preventDefault();
        if (readOnly || !draft || conflict || label.length > 500) return;
        const trimmed = label.trim();
        const next = { ...shown };
        if (trimmed) next.label = trimmed;
        else delete next.label;
        onSave(trimmed || tone !== "neutral" ? next : undefined);
      }}
    >
      <h3 className="text-sm font-semibold text-pb-text">
        <span className={severityClass[selection.severity]}>
          {selection.severity}
        </span>{" "}
        · {selection.level}
      </h3>
      <FormField label="Escalation action">
        <Textarea
          aria-label={
            selection.severity + " " + selection.level + " escalation action"
          }
          value={label}
          maxLength={500}
          rows={6}
          disabled={readOnly}
          {...labelLease}
          onChange={(event) => update({ label: event.target.value })}
          placeholder="Describe escalation action…"
        />
      </FormField>
      <details className="rounded-lg border border-pb-border p-3">
        <summary className="cursor-pointer text-sm text-pb-text">
          Appearance{" "}
          <span className="ms-2 text-xs capitalize text-pb-text-muted">
            {tone}
          </span>
        </summary>
        <div
          role="group"
          aria-label="Response tone"
          className="mt-3 flex flex-wrap gap-2"
        >
          {(["neutral", "watch", "warn", "critical"] as EscalationTone[]).map(
            (value) => (
              <Button
                key={value}
                type="button"
                size="sm"
                variant="outline"
                disabled={readOnly}
                aria-pressed={tone === value}
                {...toneLease}
                className={cn(
                  toneClass[value],
                  tone === value && "ring-2 ring-pb-teal",
                )}
                onClick={() => update({ tone: value })}
              >
                {value[0]!.toUpperCase() + value.slice(1)}
              </Button>
            ),
          )}
        </div>
      </details>
      {conflict && (
        <p role="alert" className="text-sm text-pb-text">
          This response changed while you were editing. Cancel to reload the
          latest version.
        </p>
      )}
      {!readOnly && (
        <div className={EXPANDED_EDITOR_FOOTER}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-pb-red-text"
            disabled={!cell || !!draft}
            onClick={() => update({ label: "", tone: "neutral" })}
          >
            Clear response
          </Button>
          <div className={EXPANDED_EDITOR_ACTIONS}>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!draft || conflict || label.length > 500}
            >
              Save changes
            </Button>
          </div>
        </div>
      )}
    </form>
  );
}
